import type { en } from "./en";

/**
 * French.
 *
 * Typed against the English catalogue, so a missing key is a compile error
 * rather than a screen that reads half in one language and half in another.
 *
 * The register is deliberate: **vous** throughout. The reader is a teacher or
 * the person who runs a studio or a school, at work; the app the students hold
 * says "tu", this does not.
 *
 * Typography follows French rules: a no-break space (U+00A0) before : ; ? ! and
 * %, and inside « guillemets ». Terminology matches Composer and the app: a beat
 * is an « étape », the device a « carte », a lesson a « cours ».
 */
export const fr: Record<keyof typeof en, string> = {
  // ── cadre ─────────────────────────────────────────────────────────
  "app.name": "Spontar Backstage",
  "nav.dashboard": "Vue d’ensemble",
  "nav.people": "Personnes",
  "nav.groups": "Groupes",
  "nav.devices": "Cartes",
  "nav.composer": "Composer",
  "devices.title": "Cartes",
  "devices.intro": "Les cartes des élèves qui appartiennent à votre établissement. Chacune est enregistrée ici une seule fois, à l’ouverture de la boîte. Une carte n’a pas de compte et ne se connecte jamais : on la donne à la personne présente dans la salle, et l’enseignant lui attribue un rôle pendant le cours.",
  "devices.enrol": "Enregistrer une carte à la main",
  "devices.enrolHelp": "Pour une carte qui ne peut pas être branchée sur cet ordinateur. Tout ce qui est demandé ci-dessous est imprimé sur la carte ; rien n’est installé ici.",
  "devices.none": "Aucune carte enregistrée pour l’instant",
  "devices.noneBody":
    "Les cartes s’ajoutent en général par USB, depuis la page Ajouter des cartes, une fois qu’un kit existe. Une carte enregistrée est admise à tous les cours de l’établissement.",
  "devices.column.label": "Étiquette",
  "devices.column.id": "Carte",
  "devices.column.enrolled": "Enregistrée le",
  "devices.column.state": "État",
  "devices.column.actions": "Actions",
  "devices.inService": "En service",
  "devices.notSeen": "Pas vue depuis plus de {days} jours",
  "devices.withdraw": "Retirer",
  "devices.withdrawnOn": "Retirée le {date}",
  "devices.field.label": "Étiquette",
  "devices.field.labelHint": "Ce qui est écrit sur le boîtier, pour qu’un enseignant puisse retrouver la carte que quelqu’un a en main.",
  "devices.field.id": "Numéro de carte",
  "devices.field.key": "Clé",
  "devices.field.keyHint": "Le long code imprimé à côté du numéro de carte.",

  // ── cartes : ajout par USB (K2) ──────────────────────────────────
  "devices.column.kit": "Kit",
  "devices.noKit": "—",
  "devices.add.link": "Ajouter des cartes",
  "devices.add.title": "Ajouter des cartes",
  "devices.add.intro": "Branchez une carte sur cet ordinateur avec un câble USB, choisissez le kit auquel elle appartient et appuyez sur le bouton. La carte reçoit le logiciel à jour, le Wi-Fi de son kit et une étiquette — vous n’avez rien à saisir.",
  "devices.add.browser": "Cette page nécessite Chrome ou Edge sur un ordinateur portable ou de bureau : ce sont les navigateurs capables de communiquer par câble USB. Ouvrez-la dans l’un d’eux et branchez la carte.",
  "devices.add.back": "Retour aux cartes",
  "devices.add.noKits": "Aucun kit pour l’instant",
  "devices.add.noKitsBody": "Une carte appartient à un kit — la boîte qui contient le routeur auquel elle se connectera. Créez d’abord le kit.",
  "devices.add.kit": "Kit",
  "devices.add.kitHint": "La carte ne trouvera que le Wi-Fi de ce kit. Son étiquette commence par le nom du kit.",
  "devices.add.firmware": "La version {version} du logiciel sera installée sur la carte.",
  "devices.add.firmwareUnavailable": "Le logiciel de la carte n’a pas pu être chargé depuis cette page. Rechargez la page et réessayez ; si le problème persiste, prévenez Spontar.",
  "devices.add.retry": "Réessayer",
  "devices.add.resume": "Continuer sans réinstaller le logiciel",
  "devices.add.startOver": "Réinstaller le logiciel",
  "devices.add.resumeHint": "Le logiciel est déjà sur cette carte. Débranchez-la, rebranchez-la et continuez — inutile de l’installer une deuxième fois.",
  "devices.add.start": "Ajouter la carte branchée",
  "devices.add.steps": "Étapes",
  "devices.add.step.flash": "Installer le logiciel",
  "devices.add.step.network": "Transmettre le Wi-Fi du kit",
  "devices.add.step.identity": "Lire le numéro de la carte",
  "devices.add.step.enrol": "Enregistrer la carte",
  "devices.add.state.pending": "En attente",
  "devices.add.state.running": "En cours…",
  "devices.add.state.writing": "Installation, {percent} %",
  "devices.add.state.done": "Terminé",
  "devices.add.state.failed": "Échec",
  "devices.add.screenDark": "Cette carte est enregistrée, mais son écran ne s’est pas allumé : elle ne pourra pas servir en cours. Branchez-la et ajoutez-la de nouveau avec le logiciel le plus récent ; si l’écran reste éteint, il s’agit d’une carte que ce logiciel ne connaît pas.",
  "devices.add.done": "{label} est prête. Débranchez-la, écrivez l’étiquette sur le boîtier et branchez la suivante.",
  "devices.add.count": "Ajoutées à {kit} aujourd’hui : {count}.",
  "devices.add.error.port": "Impossible de communiquer avec la carte. Débranchez-la, rebranchez-la et réessayez.",
  "devices.add.error.portBusy": "Un autre programme utilise la carte. Fermez-le — un moniteur série, un autre onglet — et réessayez.",
  "devices.add.error.noResponse": "La carte ne répond pas. Essayez un autre câble ou un autre port USB : certains câbles ne servent qu’à la recharge. Si elle ne répond toujours pas, maintenez le bouton de réinitialisation de la carte enfoncé pendant que vous appuyez sur le bouton ici.",
  "devices.add.error.wrongBoard": "Cette page ne sait pas configurer ce type d’appareil.",
  "devices.add.error.firmwareMissing": "Le logiciel de la carte n’est pas encore publié sur cette page. Prévenez Spontar.",
  "devices.add.error.flash": "Le logiciel n’a pas pu être installé. Débranchez la carte, rebranchez-la et réessayez.",
  "devices.add.error.noImprov": "La carte n’a pas démarré après l’installation du logiciel. Débranchez-la, rebranchez-la et réessayez.",
  "devices.add.error.networkNotSeen": "La carte ne capte aucun réseau nommé {ssid}. Allumez le routeur du kit à côté d’elle et attendez une minute — et vérifiez que le routeur utilise bien le nom et le mot de passe Wi-Fi du kit, en 2,4 GHz. Réseaux captés par la carte : {seen}",
  "devices.add.error.networkNotSeenNothing": "La carte ne capte aucun Wi-Fi, pas même {ssid}. Allumez le routeur du kit à côté d’elle et attendez une minute.",
  "devices.add.error.networkRefused": "La carte capte {ssid}, mais la connexion a été refusée. C’est presque toujours le mot de passe : créez-en un nouveau sur la page Kits et réglez le routeur en conséquence. Un réseau uniquement en 5 GHz produirait le même effet ; ces cartes fonctionnent en 2,4 GHz.",
  "devices.add.error.networkUnreachable": "La carte n’a pas pu rejoindre le Wi-Fi du kit. Allumez le routeur du kit à proximité, attendez une minute et réessayez.",
  "devices.add.error.network": "La carte n’a pas accepté le Wi-Fi. Réessayez ; si le problème persiste, prévenez Spontar.",
  "devices.add.error.identity": "La carte n’a pas transmis son numéro. Débranchez-la, rebranchez-la et réessayez.",
  "devices.add.error.enrol": "La carte n’a pas pu être enregistrée. Vérifiez votre connexion et réessayez.",

  // ── kits ──────────────────────────────────────────────────────────
  "nav.kits": "Kits",
  "kits.title": "Kits",
  "kits.intro": "Un kit, c’est une boîte pour une salle : un petit routeur avec son propre Wi-Fi, les cartes et une mallette. Le nom et le mot de passe du Wi-Fi sont générés ici quand vous créez le kit — personne n’a à les inventer — et affichés ici pour configurer le routeur et l’iPad de l’enseignant. Ils ne sont jamais envoyés par e-mail.",
  "kits.new": "Nouveau kit",
  "kits.newHelp": "Donnez-lui le nom qui sera écrit sur la boîte. Chaque carte qui y est ajoutée reçoit une étiquette commençant par ce nom : « Lot B, 07 ».",
  "kits.create": "Créer le kit",
  "kits.created": "{name} est prêt. Le nom et le mot de passe de son Wi-Fi figurent ci-dessous.",
  "kits.none": "Aucun kit pour l’instant",
  "kits.noneBody":
    "Créez-en un pour la boîte de chaque salle. Le nom et le mot de passe du Wi-Fi sont générés pour vous ; les cartes de la boîte s’enregistrent séparément, sous Cartes.",
  "kits.field.name": "Nom",
  "kits.field.nameHint": "Court, tel qu’il sera écrit sur la boîte et sur chacune de ses cartes.",
  "kits.field.router": "Routeur",
  "kits.field.routerHint": "L’app de l’enseignant peut configurer ce routeur d’un seul geste.",
  "kits.router.gl-mt3000": "GL.iNet Beryl AX (GL-MT3000)",
  "kits.createdOn": "Créé le {date}",
  "kits.routerOnly": "Routeur : {model}. C’est celui que l’app de l’enseignant peut configurer d’un seul geste.",
  "kits.devices": "Cartes",
  "kits.devicesOwner": "Les cartes de ce kit s’enregistrent sous Cartes, une par une, par USB.",
  "kits.devicesTeacher": "Les cartes de ce kit sont enregistrées sous Cartes par la personne qui gère Spontar dans votre établissement. Vous n’avez rien à faire ici.",
  "kits.network": "Nom du Wi-Fi",
  "kits.password": "Mot de passe du Wi-Fi",
  "kits.showPassword": "Afficher",
  "kits.hidePassword": "Masquer",
  "kits.copy": "Copier",
  "kits.copied": "Copié.",
  "kits.copyFailed": "Copie impossible. Recopiez-le depuis l’écran.",
  "kits.rename": "Renommer",
  "kits.renameHelp": "Renommer le kit ne modifie pas les étiquettes déjà écrites sur ses cartes.",
  "kits.setNetwork": "Régler le Wi-Fi",
  "kits.field.ssid": "Nom du Wi-Fi",
  "kits.field.psk": "Mot de passe du Wi-Fi",
  "kits.network.help": "Laissez ces valeurs telles quelles et réglez le routeur pour qu’il corresponde. Si la salle a déjà un routeur que vous préférez ne pas modifier, saisissez plutôt ici ses réglages actuels.",
  "kits.network.consequence": "Chaque carte déjà ajoutée à ce kit garde l’ancien réseau : il faudra la rebrancher pour lui transmettre le nouveau.",
  "kits.network.saved": "{name} utilise désormais son nouveau réseau.",
  "kits.newPassword": "Nouveau mot de passe",
  "kits.rotate.title": "Nouveau mot de passe Wi-Fi pour {name}",
  "kits.rotate.body": "Le routeur et chaque carte de ce kit devront être reconfigurés avec le nouveau mot de passe avant le prochain cours. Le nom du Wi-Fi reste le même. Saisissez le nom du kit pour confirmer.",
  "kits.rotate.confirm": "Nom du kit",
  "kits.rotate.mismatch": "Ce n’est pas le nom du kit.",
  "kits.passwordRotated": "{name} a un nouveau mot de passe Wi-Fi. Reconfigurez le routeur et ses cartes.",
  "kits.checklist.title": "Réglages du routeur",
  "kits.checklist.intro": "L’app de l’enseignant applique ces réglages d’un seul geste sur un routeur qu’elle connaît. Sur un autre routeur, ou à la main, réglez exactement ceci :",
  "kits.checklist.ssid": "Nom du Wi-Fi :",
  "kits.checklist.psk": "Mot de passe du Wi-Fi :",
  "kits.checklist.band24": "2,4 GHz activé, canal fixe {channels} — choisissez-en un.",
  "kits.checklist.band5": "5 GHz activé, mêmes nom et mot de passe.",
  "kits.checklist.isolation": "Isolation des clients désactivée — les cartes doivent pouvoir voir l’iPad de l’enseignant.",
  "kits.checklist.internetCheck": "Vérification de la connexion Internet désactivée — le routeur ne doit pas signaler qu’il est hors ligne.",
  "kits.checklist.noInternet": "Pas de connexion Internet. Le kit fonctionne de façon autonome.",
  "kits.firmware.title": "Logiciel des cartes",
  "kits.firmware.version": "Les cartes ajoutées depuis cette page reçoivent la version {version}.",
  "kits.firmware.unavailable": "Le logiciel des cartes n’a pas pu être chargé depuis cette page.",
  "kits.firmware.placeholder": "Aucun logiciel de carte n’est encore publié sur cette page. Impossible d’ajouter des cartes tant qu’il ne l’est pas.",
  "kits.firmware.nothingToInstall": "Les nouvelles cartes reçoivent cette version au moment de leur ajout. Vous n’avez rien à installer.",
  "kits.firmware.manifestLink": "Manifeste du logiciel, pour votre service informatique",
  "kit.error.notFound": "Ce kit est introuvable.",
  "kit.error.nameEmpty": "Un kit doit avoir un nom.",
  "kit.error.nameTooLong": "Ce nom est trop long.",
  "kit.error.ssidEmpty": "Un réseau doit avoir un nom.",
  "kit.error.ssidTooLong": "Un nom de Wi-Fi compte au plus 32 caractères.",
  "kit.error.pskTooShort": "Un mot de passe Wi-Fi doit compter au moins 8 caractères.",
  "kit.error.pskTooLong": "Un mot de passe Wi-Fi compte au plus 63 caractères.",
  "enrolment.error.labelRequired": "Une étiquette est nécessaire pour enregistrer une carte à la main.",
  "nav.library": "Bibliothèque",
  "nav.sessions": "Cours",
  "nav.reports": "Rapports",
  "nav.institution": "Établissement",
  "nav.billing": "Facturation",
  "nav.legal": "Juridique",
  "nav.consent": "Consentement",
  "nav.signOut": "Se déconnecter",
  "nav.menu": "Menu",
  "nav.skipToContent": "Aller au contenu",
  "nav.openComposer": "Ouvrir Composer",
  "nav.openComposerHint": "Là où les enseignants écrivent les scènes. Mêmes identifiants.",

  // ── connexion ─────────────────────────────────────────────────────
  "auth.signIn": "Se connecter",
  "auth.email": "E-mail",
  "auth.username": "Nom d’utilisateur",
  "auth.password": "Mot de passe",
  "auth.signingIn": "Connexion…",
  "auth.identifier": "E-mail ou nom d’utilisateur",
  "auth.identifierHint":
    "Celui qui figure sur votre fiche d’identifiants. Un nom d’utilisateur ne contient pas de @.",
  "auth.action.title": "Choisissez votre mot de passe",
  "auth.action.checking": "Vérification de votre lien…",
  "auth.action.for": "Pour {email}. Le même mot de passe ouvre Backstage et Composer.",
  "auth.action.save": "Enregistrer le mot de passe et se connecter",
  "auth.action.invalid":
    "Ce lien a expiré ou a déjà servi. Un lien ne fonctionne qu’une fois, pendant une heure. Demandez-en un nouveau sur la page de connexion, sous « Mot de passe oublié ? », ou auprès de la personne qui a créé votre compte.",
  "auth.action.done": "Votre mot de passe est enregistré. Vous pouvez maintenant vous connecter.",
  "auth.action.toSignIn": "Aller à la connexion",
  "home.teacher.groups": "Vos groupes",
  "home.teacher.noGroups": "Aucun groupe ne vous est encore attribué. C’est la personne qui gère Spontar dans votre établissement qui vous y ajoute.",
  "home.teacher.allGroups": "Tous les groupes",
  "home.teacher.students": "{count} élèves",
  "home.teacher.next": "Là où se fait le travail",
  "home.teacher.composerBody": "Écrivez les scènes que vous allez enseigner. Vos identifiants y fonctionnent aussi.",
  "home.teacher.lessons": "Cours",
  "home.teacher.lessonsBody": "Ce qui s’est passé pendant un cours, à relire après coup. Le cours lui-même se déroule dans l’app de l’enseignant, sur votre tablette.",
  "home.teacher.kits": "Kits",
  "home.teacher.kitsBody": "Le nom et le mot de passe du Wi-Fi de la boîte de votre salle, pour le routeur et votre tablette.",
  "auth.sameAccount": "Mêmes identifiants que pour Composer.",
  "auth.forgotPassword": "Mot de passe oublié ?",
  "auth.forgotPasswordHelp":
    "Saisissez votre adresse e-mail ci-dessus et nous vous enverrons un lien.",
  "auth.sendResetLink": "Envoyer un lien de réinitialisation",
  "auth.resetLinkSent":
    "Si cette adresse correspond à un compte, un lien vient d’y être envoyé. Il expire dans une heure.",
  "auth.resetNeedsEmail":
    "Ce compte se connecte avec un nom d’utilisateur et n’a pas d’adresse e-mail : il n’y a donc nulle part où envoyer un lien. La personne qui gère Spontar dans votre établissement peut vous délivrer de nouveaux identifiants en quelques secondes.",
  "auth.changePassword": "Choisissez votre propre mot de passe",
  "auth.changePasswordWhy":
    "Le mot de passe qui vous a été remis était imprimé sur papier. Remplacez-le avant toute autre chose.",
  "auth.newPassword": "Nouveau mot de passe",
  "auth.confirmPassword": "Confirmer le mot de passe",
  "password.tooShort": "Au moins huit caractères.",
  "password.needsUppercase": "Il faut une majuscule.",
  "password.needsLowercase": "Il faut une minuscule.",
  "password.needsNumber": "Il faut un chiffre.",
  "password.rules": "Au moins huit caractères, dont une majuscule, une minuscule et un chiffre.",
  "auth.passwordMismatch": "Les deux mots de passe ne correspondent pas.",
  "auth.save": "Enregistrer",

  // ── rôles ─────────────────────────────────────────────────────────
  "role.owner": "Responsable",
  "role.teacher": "Enseignant",
  "role.student": "Élève",
  "role.spontar_admin": "Spontar",

  // ── commun ────────────────────────────────────────────────────────
  "common.save": "Enregistrer",
  "common.cancel": "Annuler",
  "common.close": "Fermer",
  "common.delete": "Supprimer",
  "common.confirm": "Confirmer",
  "common.edit": "Modifier",
  "common.add": "Ajouter",
  "common.search": "Rechercher",
  "common.loading": "Chargement…",
  "common.saving": "Enregistrement…",
  "common.saved": "Enregistré",
  "common.retry": "Réessayer",
  "common.language": "Langue",
  "common.appLanguage": "Langue de l’application",
  "common.none": "Aucun",
  "common.yes": "Oui",
  "common.no": "Non",
  "common.back": "Retour",
  "common.required": "Obligatoire",
  "common.optional": "Facultatif",
  "common.opensInNewTab": "s’ouvre dans un nouvel onglet",
  "toast.repeat": "×{count}",

  // ── vue d’ensemble ────────────────────────────────────────────────
  "dashboard.title": "Vue d’ensemble",
  "dashboard.welcome": "Bonjour, {name}.",
  "dashboard.empty": "Rien ne s’est encore passé. Commencez par ajouter un groupe.",

  // ── prise en main ─────────────────────────────────────────────────
  "onboarding.title": "Pour commencer",
  "onboarding.intro":
    "Cinq choses, dans l’ordre. Chacune renvoie à l’endroit où elle se fait ; la liste se met à jour toute seule et disparaît quand tout est fait.",
  "onboarding.progress": "{done} sur {total} terminées",
  "onboarding.dismiss": "Masquer pour l’instant",
  "onboarding.showAgain": "Afficher de nouveau les étapes de mise en place",
  "onboarding.step.legal": "Accepter les documents juridiques",
  "onboarding.step.legal.hint":
    "{count} document reste à accepter. Aucun cours ne peut commencer tant qu’il ne l’est pas.",
  "onboarding.step.legal.hintPlural":
    "{count} documents restent à accepter. Aucun cours ne peut commencer tant qu’ils ne le sont pas.",
  "onboarding.step.teachers": "Ajouter vos enseignants",
  "onboarding.step.teachers.hint":
    "Chaque enseignant ajouté par e-mail reçoit un lien et choisit son propre mot de passe ; un enseignant sans adresse s’ajoute en important une liste de classe et reçoit un mot de passe imprimé. Vous êtes déjà enseignant : inutile de vous ajouter.",
  "onboarding.step.group": "Ajouter des élèves et les placer dans un groupe",
  "onboarding.step.group.hintNoPlayers":
    "Ajoutez les élèves un par un ou importez une liste de classe. Un élève n’a pas de mot de passe : il tient une carte pendant le cours.",
  "onboarding.step.group.hintNoGroup":
    "Un groupe, c’est une classe ou un atelier. Chaque cours se fait avec un groupe.",
  "onboarding.step.group.hintEmptyGroup": "Personne n’est encore inscrit dans {name}. Ajoutez-y des élèves.",
  "onboarding.step.devices": "Préparer un kit et ses cartes",
  "onboarding.step.devices.hint":
    "Un kit, c’est la boîte d’une salle : son propre Wi-Fi et les cartes des élèves. Créez d’abord le kit ; les cartes s’y ajoutent ensuite.",
  "onboarding.step.composer": "Ouvrir Composer et écrire votre première scène",
  "onboarding.step.composer.hint":
    "Composer est l’app où les enseignants écrivent les scènes. Vos identifiants y fonctionnent aussi.",
  "onboarding.teacher.title": "Ce que vous pouvez faire ici",
  "onboarding.teacher.intro":
    "Backstage, c’est là que se gère votre établissement : les personnes, les groupes, les cartes et ce qui s’est passé lors des cours précédents. Trois rubriques comptent pour un enseignant :",
  "onboarding.teacher.groups": "Groupes",
  "onboarding.teacher.groupsBody": "Les classes et ateliers que vous animez, avec la liste des inscrits de chacun.",
  "onboarding.teacher.lessons": "Cours",
  "onboarding.teacher.lessonsBody":
    "Ce qui s’est passé pendant un cours, à relire après coup. Le cours lui-même se déroule avec l’app de l’enseignant, sur votre tablette.",
  "onboarding.teacher.kits": "Kits",
  "onboarding.teacher.kitsBody":
    "Le nom et le mot de passe du Wi-Fi de la boîte de votre salle, pour le routeur et votre tablette.",
  "onboarding.teacher.composerBody":
    "Les scènes que vous enseignez s’écrivent dans Composer, une app distincte. Votre nom d’utilisateur et votre mot de passe y fonctionnent aussi.",
  "onboarding.teacher.dismiss": "Compris",

  // ── erreurs ───────────────────────────────────────────────────────
  "error.unexpected": "Une erreur s’est produite. Elle a été consignée.",
  "error.network.unavailable":
    "Impossible de joindre le serveur. Vérifiez votre connexion et réessayez.",
  "error.server.broken":
    "Un problème est survenu de notre côté. Ce n’est pas votre connexion — nous avons été prévenus. Réessayez dans quelques minutes.",
  "error.auth.required": "Veuillez vous connecter.",
  "error.auth.mustChangePassword": "Choisissez votre propre mot de passe avant de continuer.",
  "error.auth.invalidCredentials": "Identifiant ou mot de passe incorrect.",
  "error.permission.denied": "Vous n’y avez pas accès.",
  "error.permission.disabled": "Ce compte a été désactivé.",
  "error.permission.noInstitution": "Ce compte n’est rattaché à aucun établissement.",
  "error.user.notFound": "Cette personne est introuvable.",
  "error.user.cannotDisableSelf": "Vous ne pouvez pas désactiver votre propre compte.",
  "error.user.cannotDeleteSelf": "Vous ne pouvez pas supprimer votre propre compte.",
  "error.user.cannotChangeOwnRole": "Vous ne pouvez pas modifier votre propre rôle.",
  "error.user.disabled": "Ce compte est désactivé.",
  "error.institution.notFound": "Établissement introuvable.",
  "error.group.notFound": "Ce groupe est introuvable.",
  "error.group.needsATeacher": "Un groupe doit avoir au moins un enseignant.",
  "error.group.lastTeacher": "C’est le seul enseignant du groupe. Ajoutez-en d’abord un autre.",
  "error.group.notATeacher": "Cette personne n’est pas enseignante.",
  "error.group.notAStudent": "Cette personne n’est pas élève.",
  "error.group.mustTeachOwnGroup": "Vous ne pouvez créer qu’un groupe dont vous êtes enseignant.",
  "error.validation.invalidInput": "Un élément de ce formulaire n’est pas valide.",
  "error.validation.displayNameEmpty": "Un nom est nécessaire.",
  "error.validation.displayNameTooLong": "Ce nom est trop long.",
  "error.validation.email": "Cela ne ressemble pas à une adresse e-mail.",
  "error.validation.groupNameEmpty": "Un groupe doit avoir un nom.",
  "error.validation.groupNameTooLong": "Ce nom est trop long.",
  "error.validation.seatsExceedTier": "C’est plus d’enseignants que la formule {tier} n’en autorise.",
  "error.validation.validUntilInPast": "Cette date est déjà passée.",
  "error.validation.date": "Cette date n’est pas valide.",
  "error.validation.rangeReversed": "La fin de la période précède son début.",
  "error.validation.reasonRequired": "Merci d’indiquer un motif.",
  "error.validation.retentionOutOfRange": "Entre {min} et {max} jours.",
  "error.validation.retentionTooShort": "C’est plus court que la durée minimale de conservation des données.",
  "error.validation.retentionTooLong": "C’est plus long que la durée pendant laquelle nous conservons les données.",
  "error.forbidden.title": "Accès réservé",
  "error.forbidden.bodyNamed":
    "La page {page} est réservée au responsable de l’établissement. Si vous pensez devoir y avoir accès, adressez-vous à la personne qui gère Spontar dans votre établissement.",
  "error.forbidden.back": "Retour à la vue d’ensemble",
  "error.forbidden.body":
    "Cette page est réservée aux responsables d’établissement. Si vous pensez devoir y avoir accès, adressez-vous au responsable de votre établissement.",
  "error.notFound.title": "Il n’y a rien ici",
  "error.notFound.body": "Le lien est peut-être ancien, ou la page a été déplacée.",

  // ── erreurs de facturation ────────────────────────────────────────
  "billing.error.seatLimit":
    "Votre formule {tier} couvre {max} enseignants, et vous en avez déjà {used}. Passez à une formule supérieure pour en ajouter un autre.",
  "billing.error.studentLimit":
    "Votre licence couvre {max} élèves et {used} sont déjà inscrits. Écrivez-nous pour en avoir davantage.",
  "billing.error.licenceExpired": "La licence a expiré le {validUntil}.",
  "billing.error.suspended": "Cet établissement a été suspendu.",
  "billing.error.noSubscription": "Il n’y a pas encore d’abonnement à gérer.",
  "billing.error.tooManyTeachersForTier":
    "Cette formule couvre {max} enseignants, et vous en avez {used}. Retirez-en d’abord quelques-uns.",
  "billing.error.vatIdMalformed": "Ce numéro de TVA n’a pas le bon format.",
  "billing.error.vatIdCountryMismatch": "Ce numéro de TVA appartient à un autre pays.",
  "billing.error.vatIdNotFound": "VIES ne reconnaît pas ce numéro de TVA.",
  "billing.error.vatValidationUnavailable":
    "VIES ne répond pas. La TVA sera facturée tant que le numéro n’aura pas pu être vérifié ; réessayez plus tard.",

  // ── erreurs juridiques et de cours ────────────────────────────────
  "legal.error.documentsOutstanding": "Certains documents doivent encore être acceptés.",
  "legal.error.consentMissing": "Le consentement parental manque pour certains élèves.",
  "legal.error.versionSuperseded": "Une version plus récente a été publiée. Rechargez la page et lisez-la.",
  "legal.error.documentNotFound": "Ce document est introuvable.",
  "session.error.notFound": "Ce cours est introuvable.",
  "session.error.notHost": "Seul l’enseignant qui anime un cours peut le piloter.",
  "session.error.reviewIsTeacherOnly": "Seul l’enseignant qui a animé le cours peut en ouvrir le détail.",
  "session.error.groupArchived": "Ce groupe est archivé.",
  "session.error.alreadyEnded": "Ce cours est déjà terminé.",
  "session.error.codeNotFound": "Aucun cours actif ne correspond à ce code.",
  "session.error.notOnRoster": "Vous n’êtes pas inscrit dans ce groupe.",

  // ── erreurs de contenu et d’import ────────────────────────────────
  "scenario.error.notFound": "Cette scène est introuvable.",
  "scenario.error.staleEdit": "{editedBy} a enregistré des modifications pendant que vous travailliez. Rechargez d’abord la page.",
  "scenario.error.invalid": "Cette scène ne peut pas encore être publiée.",
  "scenario.error.versionSuperseded": "Une version plus récente existe.",
  "import.error.fileTooLarge": "Ce fichier est trop volumineux. Une liste de classe n’est jamais aussi volumineuse.",
  "import.error.emptyFile": "Ce fichier est vide.",
  "import.error.unsupportedFileType": "Spontar lit les fichiers CSV et XLSX.",
  "import.error.noReadableRows": "Rien de lisible n’a été trouvé dans ce fichier.",
  "import.error.tooManyRows": "Le fichier dépasse {max} lignes.",
  "import.error.nothingToImport": "Il n’y a rien à importer.",
  "import.error.ageBandRequiredForNewGroup":
    "Le groupe {group} n’existe pas encore, et un nouveau groupe a besoin d’une tranche d’âge.",
  "import.error.noOwnerForNewGroup": "Un nouveau groupe doit être confié à un responsable.",
  "ai.error.budgetExceeded": "Le budget IA de ce mois-ci est épuisé.",
  "ai.error.providerUnavailable": "L’assistant d’écriture ne répond pas. Réessayez dans un instant.",
  "ai.error.unparseableOutput": "L’assistant a renvoyé un résultat inutilisable. Réessayez.",
  "ai.error.generatedScenarioInvalid":
    "L’assistant a produit une scène qui s’interromprait en plein milieu. Essayez avec une autre consigne.",
  "ai.error.studentDataBlocked": "Cette demande a été bloquée car elle contenait des données d’élèves.",
  "enrolment.error.notFound": "Cette carte n’est pas enregistrée.",
  "contact.error.email": "Cela ne ressemble pas à une adresse e-mail.",
  "contact.error.missingFields": "Veuillez remplir les champs obligatoires.",

  // ── paramètres de l’établissement ─────────────────────────────────
  "institution.title": "Établissement",
  "institution.section.identity": "Identité",
  "institution.section.defaults": "Valeurs par défaut des nouveaux groupes",
  "institution.section.retention": "Durée de conservation des données",
  "institution.section.billing": "Coordonnées de facturation",
  "institution.name": "Nom",
  "institution.country": "Pays",
  "institution.countryFixed":
    "Le pays détermine les documents juridiques applicables : un établissement ne peut donc pas le modifier lui-même. Écrivez-nous et nous ferons le changement.",
  "institution.segment": "Type d’établissement",
  "institution.segment.studio": "Studio",
  "institution.segment.school": "École",
  "institution.locale": "Langue",
  "institution.locale.en": "Anglais",
  "institution.locale.de": "Allemand",
  "institution.locale.fr": "Français",
  "institution.localeHint":
    "La langue des fiches d’identifiants et des e-mails que nous envoyons. Chaque personne peut toujours choisir la sienne.",
  "institution.defaultSessionMode": "Mode de connexion par défaut des cours",
  "institution.mode.online": "En ligne",
  "institution.mode.offline": "Hors ligne",
  "institution.modeHint":
    "En mode hors ligne, tout le cours se déroule sur le réseau local. Un enseignant peut choisir l’un ou l’autre au moment de commencer.",
  "institution.assessmentPreset": "Référentiel d’évaluation",
  "institution.preset.ncas": "NCAS (États-Unis)",
  "institution.preset.aoao": "AO1–AO4 (Angleterre et pays de Galles)",
  "institution.preset.epa": "EPA / KMK (Allemagne)",
  "institution.preset.custom": "Personnalisé",
  "institution.retentionDays": "Les données des cours sont conservées pendant",
  "institution.retentionUnit": "jours",
  "institution.retentionHint":
    "Qui a joué quel rôle, les évaluations données par l’enseignant, les notes qu’il a prises. Les statistiques sont conservées sans limite de durée et ne contiennent aucun nom.",
  "institution.retentionShortWarning":
    "En dessous de 90 jours, un enseignant perd les données du trimestre précédent avant la fin du trimestre en cours. C’est possible, mais c’est rarement ce que l’on souhaite.",
  "institution.billingEmail": "Adresse d’envoi des factures",
  "institution.vatId": "Numéro de TVA intracommunautaire",
  "institution.vatIdHint": "Vérifié auprès de VIES. Un numéro non vérifié est considéré comme absent.",
  "institution.vat.validated": "Vérifié le {date}. L’autoliquidation s’applique.",
  "institution.vat.none": "Pas de numéro de TVA. La TVA est facturée au taux de votre pays.",
  "institution.vat.outsideScope": "Hors du champ de la TVA européenne.",
  "institution.vat.charge": "La TVA est facturée.",
  "institution.vat.revalidate": "Vérifier de nouveau",
  "institution.vat.remove": "Supprimer",
  "institution.saved": "Enregistré.",

  // ── juridique ─────────────────────────────────────────────────────
  "legal.title": "Juridique",
  "legal.docType.tos": "Conditions d’utilisation",
  "legal.docType.dpa": "Accord de traitement des données",
  "legal.docType.avv": "Auftragsverarbeitungsvertrag",
  "legal.docType.ferpa_addendum": "Avenant FERPA",
  "legal.docType.state_addendum": "Avenant propre à l’État",
  "legal.docType.privacy": "Politique de confidentialité",
  "legal.docType.privacy_child": "Politique de confidentialité pour les jeunes",
  "legal.docType.impressum": "Mentions légales",
  "legal.docType.parental_consent_template": "Modèle de consentement parental",
  "legal.docType.law25_addendum": "Avenant Loi 25 (Québec)",
  "legal.intro":
    "Les documents qui s’appliquent à vous dépendent de votre pays et de votre type d’établissement — ni de nous, ni de vous.",
  "legal.column.document": "Document",
  "legal.column.version": "Version",
  "legal.column.status": "Statut",
  "legal.column.published": "Publié le",
  "legal.column.actions": "Actions",
  "legal.status.accepted": "Accepté par votre établissement",
  "legal.status.newVersion": "Nouvelle version — action requise",
  "legal.status.never": "Pas encore accepté",
  "legal.status.optional": "Pour information",
  "legal.acceptedVersion": "Vous avez accepté la version {version}",
  "legal.read": "Lire",
  "legal.openOnSite": "Ouvrir la page publiée",
  "legal.accept": "Accepter",
  "legal.acceptedToast": "C’est enregistré.",
  "legal.scrollToEnd": "Lisez jusqu’au bout avant d’accepter.",
  "legal.acceptOnBehalf":
    "Votre acceptation engage {institution}. Votre nom, la version et l’heure sont enregistrés.",
  "legal.error.bodyUnavailable":
    "Le texte n’a pas pu être chargé. Ouvrez plutôt la page publiée, puis réessayez dans un instant.",
  "legal.gapsTitle": "Ce qui manque de notre côté",
  "legal.gapsBody":
    "Ces documents s’appliquent à vous et nous ne les avons pas encore publiés. C’est à nous d’y remédier, pas à vous. Nous vous écrirons quand ils seront prêts.",
  "legal.outstandingBanner":
    "{count} document attend votre acceptation. Aucun cours ne peut commencer tant qu’il n’est pas accepté.",
  "legal.outstandingBannerPlural":
    "{count} documents attendent votre acceptation. Aucun cours ne peut commencer tant qu’ils ne sont pas acceptés.",
  "legal.outstandingAction": "Les lire",
  "legal.blockingTitle": "Nouvelle version à lire",
  "legal.blockingBody":
    "Ces documents ont changé depuis que vous les avez acceptés. Merci de les lire et de les accepter avant de continuer.",
  "legal.blockingSignOut": "Se déconnecter",
  "legal.gate.firstRunTitle": "Avant de commencer",
  "legal.gate.firstRunBody":
    "La configuration de {institution} est terminée. Lisez et acceptez les conditions convenues entre votre établissement et Spontar — cela prend quelques minutes et ne se fait qu’une fois. Backstage s’ouvre dès que tout ce qui figure ci-dessous est accepté.",
  "legal.gate.readAndAccept": "Lire et accepter",
  "legal.gate.ownerOnly":
    "Seul le responsable de l’établissement peut accepter. Les enseignants pourront se connecter une fois que ce sera fait.",
  "legal.gate.teacherTitle": "Le responsable de votre établissement doit d’abord accepter les conditions",
  "legal.gate.teacherBody":
    "Avant que quiconque chez {institution} puisse utiliser Backstage, son responsable doit lire et accepter :",
  "legal.gate.teacherNext":
    "Votre compte est en ordre. Demandez au responsable de se connecter une fois et d’accepter ; vous aurez accès ensuite.",
  "legal.gate.unavailableTitle": "Nous n’avons pas pu vérifier les documents juridiques de votre établissement",
  "legal.gate.unavailableBody":
    "Backstage s’ouvrira une fois la vérification faite. Réessayez dans un instant.",

  // ── sous-traitants et AIPD ────────────────────────────────────────
  "legal.subprocessors.title": "Qui d’autre traite vos données",
  "legal.subprocessors.body":
    "Toutes les entreprises qui ont accès aux données de votre établissement, dans quel but et où. La liste publiée fait foi.",
  "legal.subprocessors.view": "Voir la liste publiée",
  "legal.dpia.title": "Analyse d’impact relative à la protection des données",
  "legal.dpia.body":
    "Une AIPD est disponible sur demande. La plupart des établissements n’en ont pas besoin pour un cours de théâtre ; une école publique dotée d’un délégué à la protection des données la demande généralement.",
  "legal.dpia.request": "Demander l’AIPD",

  // ── consentement parental ─────────────────────────────────────────
  "consent.title": "Consentement parental",
  "consent.intro":
    "Les groupes comptant des mineurs doivent avoir un consentement enregistré avant qu’un cours puisse commencer. C’est vous qui êtes en relation avec les familles ; nous ne les contactons jamais.",
  "consent.noGroups": "Aucun groupe n’a encore besoin de consentement.",
  "consent.group.recorded": "Enregistrés : {recorded}",
  "consent.group.missing": "Manquants : {count}",
  "consent.group.complete": "Complet",
  "consent.notRequired": "Non requis pour ce groupe",
  "consent.record": "Enregistrer un consentement",
  "consent.revoke": "Retirer",
  "consent.method": "Forme du consentement",
  "consent.method.paper_form": "Formulaire papier signé",
  "consent.method.digital_signature": "Signature électronique",
  "consent.method.verbal_recorded": "Donné de vive voix et consigné",
  "consent.method.school_policy": "Couvert par le règlement de l’école",
  "consent.note": "Remarque",
  "consent.noteHint": "Où le formulaire est classé, par exemple. Jamais le nom ni l’adresse d’un parent.",
  "consent.recorded": "Enregistré pour {name}.",
  "consent.revoked": "Retiré pour {name}.",
  "consent.missingHeading": "Encore manquants",
  "consent.recordedHeading": "Enregistrés",

  // ── personnes ─────────────────────────────────────────────────────
  "people.title": "Personnes",
  "people.add": "Ajouter une personne",
  "people.import": "Importer une liste",
  "people.search": "Rechercher par nom ou nom d’utilisateur",
  "people.filter.role": "Rôle",
  "people.filter.group": "Groupe",
  "people.filter.status": "Statut",
  "people.filter.all": "Tous",
  "people.filter.hasDevices": "Uniquement les personnes ayant une carte enregistrée",
  "people.column.name": "Nom",
  "people.column.role": "Rôle",
  "people.column.username": "Nom d’utilisateur",
  "people.column.groups": "Groupes",
  "people.column.devices": "Cartes",
  "people.column.lastActive": "Dernière activité",
  "people.column.status": "Statut",
  "people.status.active": "Actif",
  "people.status.disabled": "Désactivé",
  "people.status.mustChangePassword": "N’a pas encore choisi de mot de passe",
  "people.never": "Jamais",
  "people.empty":
    "Personne ne correspond",
  "people.emptyBody":
    "Modifiez les filtres, ajoutez un enseignant à la main ou importez une liste de classe.",
  "people.selected": "Sélection : {count}",
  "people.bulk.assignGroup": "Ajouter à un groupe",
  "people.bulk.resetPassword": "Réinitialiser les mots de passe",
  "people.bulk.revokeDevices": "Révoquer les cartes",
  "people.bulk.disable": "Désactiver",
  "people.bulk.delete": "Supprimer",
  "people.confirm.typeCount": "Saisissez {count} pour confirmer",
  "people.confirm.countMismatch": "Ce n’est pas le bon nombre.",
  "people.confirm.deleteTitle": "Supprimer {count} personnes",
  "people.confirm.deleteBody":
    "Leurs comptes, leurs inscriptions aux groupes et les données de leurs cours sont supprimés. Les statistiques ne sont pas touchées — elles ne contiennent aucun nom. Cette action est irréversible.",
  "people.confirm.resetTitle": "Réinitialiser les mots de passe de {count} personnes",
  "people.confirm.resetBody":
    "Leurs mots de passe actuels cessent immédiatement de fonctionner. Les nouveaux sont imprimés sur une nouvelle feuille, qu’il vous faudra distribuer.",
  "people.confirm.revokeTitle": "Révoquer les cartes de {count} personnes",
  "people.confirm.revokeBody":
    "Chaque carte devra être enregistrée de nouveau avant de pouvoir rejoindre un cours. À faire quand une carte est perdue ou qu’un élève s’en va.",
  "people.confirm.disableTitle": "Désactiver {count} personnes",
  "people.confirm.disableBody":
    "Elles ne peuvent plus se connecter. Rien n’est supprimé, et vous pouvez annuler cette action depuis la page de chaque personne.",
  "people.reason": "Motif",
  "people.reasonHint": "Consigné dans le journal d’audit. Une phrase suffit.",
  "people.intro":
    "Toutes les personnes de votre établissement : les enseignants, qui se connectent, et les élèves, qui tiennent une carte pendant le cours et ne se connectent jamais. Vous figurez comme responsable et comptez comme enseignant — inutile de vous ajouter.",
  "people.role.ownerYou": "Responsable (vous)",
  "people.mark.youOwner": "(vous, responsable)",
  "people.mark.owner": "(responsable)",

  // ── une personne ──────────────────────────────────────────────────
  "person.title": "{name}",
  "person.section.identity": "Identité",
  "person.section.groups": "Groupes",
  "person.section.devices": "Cartes",
  "person.section.data": "Ses données",
  "person.displayName": "Nom",
  "person.username": "Nom d’utilisateur",
  "people.noUsername": "Les élèves tiennent une carte enregistrée et ne se connectent jamais : ils n’ont donc pas de nom d’utilisateur.",
  "people.noUsernameMark": "—",
  "person.noUsername": "Aucun — cet élève tient une carte enregistrée et ne se connecte jamais.",
  "person.email": "E-mail",
  "person.emailNone": "Aucun. Les élèves ne reçoivent jamais d’e-mail et aucune adresse n’est collectée.",
  "person.role": "Rôle",
  "person.created": "Ajouté le",
  "person.lastActive": "Dernière activité",
  "person.noGroups": "Dans aucun groupe pour l’instant",
  "person.noDevices": "Aucune carte enregistrée",
  "person.device.enrolled": "Enregistrée le {date}",
  "person.device.lastSeen": "Vue pour la dernière fois le {date}",
  "person.device.revoked": "Révoquée le {date}",
  "person.device.revoke": "Révoquer",
  "person.resetPassword": "Réinitialiser le mot de passe",
  "person.disable": "Désactiver",
  "person.enable": "Réactiver",
  "person.export": "Télécharger toutes ses données",
  "person.delete": "Supprimer cette personne",
  "person.deleteTitle": "Supprimer {name}",
  "person.deleteBody":
    "Son compte, ses inscriptions aux groupes, ses cartes enregistrées et les données de ses cours sont supprimés. Les statistiques ne sont pas touchées — elles ne contiennent aucun nom. Cette action est irréversible.",
  "person.deleteConfirm": "Saisissez {name} pour confirmer",
  "person.deleteMismatch": "Ce n’est pas le nom.",
  "person.noHistory":
    "On accède aux données d’un cours par le cours, pas par la personne. Elles appartiennent à l’enseignant qui l’a donné.",
  "person.passwordIssued": "Nouveau mot de passe pour {name} : {password}",
  "person.passwordIssuedWarning":
    "Notez-le maintenant. Il n’est affiché qu’une fois et ne peut pas être récupéré.",
  "person.noCredentials": "{name} figure dans la liste. Cette personne tient une carte enregistrée pendant le cours et ne se connecte jamais.",

  // ── ajouter une personne ──────────────────────────────────────────
  "newPerson.title": "Ajouter une personne",
  "newPerson.role": "Quel est son rôle ?",
  "newPerson.role.teacher": "Enseignant",
  "newPerson.role.student": "Élève",
  "newPerson.name": "Son nom",
  "newPerson.email": "Son e-mail",
  "newPerson.emailHint":
    "Son lien de connexion y est envoyé, ainsi que les réinitialisations de mot de passe. Un enseignant sans adresse ne peut être ajouté qu’en important une liste de classe.",
  "newPerson.noEmailForStudents":
    "Un élève tient une carte pendant le cours et ne se connecte jamais : il n’y a donc rien à lui envoyer. Pas de mot de passe, et aucune adresse e-mail d’élève n’est collectée nulle part dans Spontar — c’est notre choix le plus important en matière de protection des données.",
  "newPerson.locale": "Langue",
  "newPerson.create": "Ajouter",
  "newPerson.created": "Personne ajoutée : {name}.",
  "newPerson.invited": "Un lien de connexion a été envoyé à {email}. Il mentionne les deux apps.",
  "newPerson.inviteNotSent":
    "{name} figure désormais dans la liste, mais l’e-mail n’a pas pu être envoyé. Écrivez-nous et nous renverrons le lien.",

  // ── import ────────────────────────────────────────────────────────
  "import.title": "Importer une liste",
  "import.step.upload": "Le fichier",
  "import.step.mapping": "Les colonnes",
  "import.step.review": "Les personnes",
  "import.step.credentials": "Les mots de passe",
  "import.upload.drop": "Déposez ici un fichier CSV ou un tableur, ou choisissez un fichier",
  "import.upload.choose": "Choisir un fichier",
  "import.upload.limit": "CSV ou XLSX, jusqu’à 2 Mo. Une liste de classe n’est jamais plus lourde.",
  "import.upload.sheet": "Quelle feuille ?",
  "import.upload.reading": "Lecture du fichier…",
  "import.upload.rowsFound": "{count} lignes, {columns} colonnes",
  "import.upload.skippedLeading":
    "{count} lignes au-dessus de l’en-tête ont été ignorées — un titre ou des lignes vides.",
  "import.mapping.title": "Ce que contient chaque colonne",
  "import.mapping.fromAi":
    "Proposé par l’assistant d’écriture. Rien n’est créé tant que vous n’avez pas vu chaque ligne.",
  "import.mapping.fromHeuristic":
    "Colonnes associées d’après leur intitulé. L’assistant d’écriture n’a pas été utilisé pour ce fichier : vérifiez donc plus attentivement que d’habitude.",
  "import.mapping.confidence.high": "Sûr",
  "import.mapping.confidence.medium": "Assez sûr",
  "import.mapping.confidence.low": "Pas sûr",
  "import.mapping.field.displayName": "Nom",
  "import.mapping.field.displayNameSplit": "Nom, sur deux colonnes",
  "import.mapping.field.role": "Rôle",
  "import.mapping.field.group": "Groupe",
  "import.mapping.field.ageBand": "Âge ou niveau",
  "import.mapping.none": "Absent de ce fichier",
  "import.mapping.first": "Première partie",
  "import.mapping.second": "Seconde partie",
  "import.mapping.splitToggle": "Le nom est réparti sur deux colonnes",
  "import.mapping.preview": "Aperçu",
  "import.mapping.continue": "Voir les personnes",
  "import.note.headerRowGuessed": "La ligne d’en-tête a été trouvée sous d’autres lignes.",
  "import.note.nameSplitAcrossColumns": "Le nom semble réparti sur deux colonnes.",
  "import.note.roleColumnAbsent": "Pas de colonne de rôle. Tout le monde sera élève, sauf indication contraire.",
  "import.note.groupColumnAbsent": "Pas de colonne de groupe. Personne ne sera placé dans un groupe.",
  "import.note.ageColumnAbsent": "Pas de colonne d’âge ou de niveau.",
  "import.note.aiUnavailableUsedHeuristic":
    "L’assistant d’écriture n’était pas disponible : les colonnes ont donc été associées d’après leur nom.",
  "import.note.lowConfidence": "Les colonnes étaient difficiles à lire. Vérifiez-les toutes.",
  "import.review.title": "Vérifiez chaque personne avant de créer les comptes",
  "import.review.intro":
    "Rien n’existe encore. Corrigez ce qui est faux, excluez les personnes qui ne devraient pas figurer ici, et seulement ensuite créez les comptes.",
  "import.review.summary": "Prêtes : {ready} · À vérifier : {attention}",
  "import.review.unreadable": "{count} lignes étaient totalement illisibles et ne sont pas affichées ci-dessous",
  "import.review.excludedCount": "Exclues : {count}",
  "import.review.column.row": "Ligne",
  "import.review.column.name": "Nom",
  "import.review.column.role": "Rôle",
  "import.review.column.group": "Groupe",
  "import.review.column.ageBand": "Tranche d’âge",
  "import.review.column.problems": "Problèmes",
  "import.review.exclude": "Exclure",
  "import.review.include": "Réintégrer",
  "import.review.commit": "Créer {count} comptes",
  "import.review.blocked": "Corrigez tout ce qui est signalé en rouge avant de créer des comptes.",
  "import.review.back": "Retour aux colonnes",
  "import.problem.noName": "Pas de nom",
  "import.problem.nameTooLong": "Plus de {max} caractères",
  "import.problem.groupNameTooLong": "Nom de groupe de plus de {max} caractères",
  "import.problem.invalidAgeBand": "Tranche d’âge non reconnue",
  "import.problem.ageBandRequired": "{group} n’existe pas encore, et un nouveau groupe a besoin d’une tranche d’âge",
  "import.problem.duplicateInGroup": "{count} personnes portent ce nom dans ce groupe",
  "import.ageBand.u13": "Moins de 13 ans",
  "import.ageBand.13-15": "13 à 15 ans",
  "import.ageBand.16-17": "16 à 17 ans",
  "import.ageBand.adult": "18 ans et plus",
  "import.ageBand.mixed": "Âges mélangés",
  "import.ageBand.none": "Non défini",
  "import.credentials.title": "{count} personnes ajoutées",
  "import.credentials.skipped": "{count} lignes n’ont pas été importées",
  "import.credentials.groupsCreated.one": "{count} nouveau groupe a été créé",
  "import.credentials.groupsCreated.other": "{count} nouveaux groupes ont été créés",
  "import.credentials.warning":
    "Les mots de passe ne sont affichés qu’une fois et ne peuvent pas être récupérés. Imprimez les feuilles maintenant. Si vous les perdez, délivrez de nouveaux mots de passe — personne ne peut les retrouver, pas même nous.",
  "import.credentials.noneNeeded": "Rien à imprimer.",
  "import.credentials.noneNeededWhy": "Toutes les personnes de cet import sont des élèves. Les élèves tiennent une carte enregistrée par l’établissement et ne se connectent jamais : il n’y a donc aucun mot de passe à distribuer.",
  "import.credentials.staffOnly": "{count} fiches, pour le personnel uniquement. Les élèves tiennent une carte enregistrée et n’ont pas de mot de passe.",
  "import.credentials.print": "Imprimer les fiches d’identifiants",
  "import.credentials.printing": "Préparation des fiches…",
  "import.credentials.opened": "Les fiches se sont ouvertes dans un nouvel onglet.",
  "import.credentials.reprint":
    "Imprimer de nouveau délivre de nouveaux mots de passe et invalide ceux de l’ancienne feuille.",
  "import.credentials.done": "Terminer",
  "import.skip.noName": "Pas de nom sur la ligne",
  "import.skip.nameTooLong": "Le nom était trop long",
  "import.skip.invalidRole": "Rôle non reconnu",
  "import.skip.groupNameTooLong": "Le nom du groupe était trop long",
  "import.skip.invalidAgeBand": "Tranche d’âge non reconnue",

  // ── groupes ───────────────────────────────────────────────────────
  "groups.title": "Groupes",
  "groups.new": "Nouveau groupe",
  "groups.showArchived": "Afficher les groupes archivés",
  "groups.empty": "Aucun groupe pour l’instant",
  "groups.emptyBody":
    "Un groupe, c’est une classe ou un atelier : ses enseignants et ses élèves inscrits. Chaque cours se fait avec un groupe. Créez le premier ici, ou importez une liste de classe sous Personnes.",
  "groups.column.name": "Nom",
  "groups.column.ageBand": "Tranche d’âge",
  "groups.column.teachers": "Enseignants",
  "groups.column.students": "Élèves",
  "groups.column.lastSession": "Dernier cours",
  "groups.column.mode": "Lieu du cours",
  "groups.archived": "Archivé",
  "groups.noSessions": "Aucun pour l’instant",
  "groups.studentCount": "{count}",

  "group.new.title": "Nouveau groupe",
  "group.name": "Nom",
  "group.nameHint": "Le nom que lui donne l’enseignant : 4e B, Mardi soir, Troupe 2.",
  "group.ageBand": "Tranche d’âge",
  "group.ageBandMinorsWarning":
    "Ce groupe compte des mineurs : un consentement parental doit donc être enregistré avant qu’un cours puisse commencer. C’est vous qui l’enregistrez ; nous ne contactons jamais les familles.",
  "group.ageBandAdults": "Aucun mineur : pas besoin de consentement parental.",
  "group.ageBandMinorsNoConsent":
    "Ce groupe compte des mineurs : leurs données sont donc traitées selon les règles applicables aux jeunes. À cet âge, là où vous êtes, aucun consentement parental n’est nécessaire.",
  "group.defaultMode": "Où a lieu le cours",
  "group.defaultModeHint": "Cela détermine si le cours a besoin du Wi-Fi du kit.",
  "group.mode.online": "Chacun chez soi",
  "group.mode.offline": "Dans une même salle (avec un kit)",
  "group.teachers": "Enseignants",
  "group.teachersHint":
    "Autant que vous le souhaitez, vous y compris. L’un d’eux anime chaque cours.",
  "group.roster": "Élèves",
  "group.rosterHint": "Facultatif pour l’instant. Vous pourrez les ajouter plus tard ou importer une liste.",
  "group.create": "Créer le groupe",
  "group.create.needsName": "Donnez d’abord un nom au groupe.",
  "group.create.needsTeacher":
    "Choisissez au moins un enseignant — un groupe sans enseignant ne peut pas faire cours.",
  "group.created": "Groupe créé : {name}.",

  "group.tab.roster": "Inscrits",
  "group.tab.teachers": "Enseignants",
  "group.tab.settings": "Paramètres",
  "group.tab.devices": "Cartes",
  "group.tab.history": "Cours",

  "group.roster.add": "Ajouter des élèves",
  "group.roster.remove": "Retirer",
  "group.roster.removeTitle": "Retirer {name} de {group}",
  "group.roster.removeBody":
    "Ses cartes pour ce groupe sont révoquées : cette personne ne pourra plus rejoindre un cours de ce groupe tant qu’elle n’aura pas été réenregistrée. Son compte et tout le reste demeurent inchangés.",
  "group.roster.removeConfirm": "Saisissez {name} pour confirmer",
  "group.roster.removed": "Personne retirée du groupe : {name}.",
  "group.roster.empty": "Personne n’est encore inscrit",
  "group.roster.addedOn": "Ajouté le {date}",
  "group.roster.added": "Ajoutés : {count}.",
  "group.roster.chooseStudents": "Quels élèves ?",
  "group.roster.noneToAdd": "Tout le monde est déjà inscrit dans ce groupe.",

  "group.teachers.add": "Ajouter un enseignant",
  "group.teachers.remove": "Retirer",
  "group.teachers.lastOne":
    "C’est le seul enseignant du groupe. Un groupe sans enseignant ne peut pas faire cours : ajoutez-en d’abord un autre.",
  "group.teachers.chooseTeacher": "Quel enseignant ?",
  "group.teachers.noneToAdd": "Tous les enseignants font déjà partie de ce groupe.",

  "group.settings.archive": "Archiver ce groupe",
  "group.settings.unarchive": "Désarchiver",
  "group.settings.archiveBody":
    "Il disparaît de la liste et de l’app. Rien n’est supprimé, et vous pouvez le rétablir à tout moment.",
  "group.settings.assessmentOverride": "Référentiel d’évaluation de ce groupe",
  "group.settings.assessmentInherit": "Celui de l’établissement",
  "group.settings.assessmentHint":
    "L’échelle que vous utilisez déjà pour évaluer. Gardez celle de l’établissement, sauf si cette classe est évaluée autrement.",

  "group.devices.empty": "Aucune carte n’est enregistrée pour les inscrits de ce groupe",
  "group.devices.stale": "Inutilisée depuis {days} jours",
  "group.devices.column.student": "Élève",
  "group.devices.column.label": "Carte",
  "group.devices.column.enrolled": "Enregistrée le",
  "group.devices.column.lastSeen": "Dernière activité",

  "group.history.empty": "Aucun cours n’a encore eu lieu avec ce groupe",
  "group.history.column.date": "Date",
  "group.history.column.teacher": "Enseignant",
  "group.history.column.mode": "Connexion",
  "group.history.column.attendance": "Présents",
  "group.history.live": "En direct",

  "group.consent.title": "Consentement parental",
  "group.consent.coverage": "{recorded} sur {total} enregistrés",
  "group.consent.complete": "Tous les inscrits de ce groupe ont un consentement enregistré.",
  "group.consent.missing": "Manquant pour {names}",
  "group.consent.record": "Enregistrer un consentement",
  "group.consent.blocks": "Aucun cours ne peut commencer tant que tous les consentements ne sont pas enregistrés.",

  // ── objectifs pédagogiques ────────────────────────────────────────
  "goal.status.title": "Statut",
  "goal.status.description":
    "Qui domine qui, et ce qui se passe quand cela bascule. La colonne vertébrale de la plupart des scènes qui fonctionnent.",
  "goal.accepting_offers.title": "Accepter les propositions",
  "goal.accepting_offers.description":
    "Prendre ce que votre partenaire vous donne et construire dessus, au lieu de le bloquer.",
  "goal.emotional_range.title": "Palette émotionnelle",
  "goal.emotional_range.description":
    "Sortir de votre registre habituel, au-delà de votre zone de confort, et y rester sincère.",
  "goal.physicality.title": "Présence physique",
  "goal.physicality.description":
    "Le corps d’abord. Ce que fait une personne avant de parler, ou au lieu de parler.",
  "goal.listening.title": "Écoute",
  "goal.listening.description":
    "Se laisser changer par ce que l’on entend. La compétence qui rend tout le reste possible.",
  "goal.text_work.title": "Travail du texte",
  "goal.text_work.description":
    "Travailler à partir d’un texte : le rythme, l’écoute de la réplique et l’intention sous les mots.",
  "goal.source": "D’après {name}",

  // ── validation des scènes (clés du serveur) ─────────────────────
  "scenario.error.roleCount": "Une scène compte entre {min} et {max} rôles.",
  "scenario.error.duplicateRoleId": "Deux rôles ont le même identifiant.",
  "scenario.error.roleUnused": "{role} n’a rien à faire.",
  "scenario.error.roleNameEmpty": "Un rôle doit avoir un nom.",
  "scenario.error.tooFewBeats": "Une scène compte au moins {min} étapes.",
  "scenario.error.cueOrder": "Les étapes ne sont pas dans l’ordre.",
  "scenario.error.cueUnknownRole": "Cette étape appartient à un rôle qui ne figure pas dans la scène.",
  "scenario.error.emotionEmpty": "Une étape a besoin d’une émotion.",
  "scenario.error.duplicateCueId": "Deux étapes ont le même identifiant.",
  "scenario.error.openingLength": "L’introduction doit compter entre 1 et {max} caractères.",
  "scenario.error.castRange": "Le nombre de joueurs ne correspond pas aux rôles de la scène.",
  "scenario.error.unknownSource": "Ce n’est pas une pratique que nous citons.",
  "scenario.error.constraintLabelEmpty": "La règle récurrente doit avoir un nom.",
  "scenario.error.consecutiveSameRole":
    "Deux étapes de suite pour le même rôle. Fusionnez-les.",
  "scenario.error.situationTooLong": "Une situation compte au plus {max} caractères.",
  "scenario.error.noCues": "Une scène compte au moins une étape.",
  "scenario.error.tooFewRoles": "Une scène compte au moins deux rôles.",
  "scenario.error.roleWithoutCues": "{role} n’a rien à faire.",
  "scenario.error.cueWithoutRole": "Une étape renvoie à un rôle qui ne figure pas dans la scène.",
  "scenario.error.castRangeInvalid": "Le nombre minimal de joueurs ne peut pas dépasser le nombre maximal.",
  "scenario.error.openingEmpty": "L’enseignant n’a rien à lire à voix haute.",
  "scenario.error.openingTooLong": "L’introduction compte au plus {max} caractères.",
  "scenario.error.titleEmpty": "Une scène doit avoir un titre.",
  "scenario.error.iconUnknown": "La carte ne peut pas afficher cette icône. Choisissez-en une dans la liste.",
  "scenario.error.lineEmpty": "Une réplique ne peut pas être vide.",
  "scenario.error.lineTooLong": "Une réplique compte au plus 400 caractères. Répartissez-la sur deux étapes.",
  "scenario.error.imageMissing": "Cette étape n’a pas encore d’image.",
  "scenario.error.captionTooLong": "Une légende compte au plus 80 caractères.",
  "scenario.error.imageOnTextBeat": "Seule une étape image peut porter une image.",

  // ── cours ─────────────────────────────────────────────────────────
  "sessions.title": "Cours",
  "sessions.empty": "Aucun cours n’a encore eu lieu",
  "sessions.emptyBody":
    "Les cours se déroulent avec l’app de l’enseignant sur une tablette, pas ici. C’est ici qu’on revient ensuite sur ce qui s’y est passé.",
  "sessions.noMatch": "Aucun cours ne correspond à ces filtres",
  "sessions.noMatchBody": "Essayez « Tous » pour le groupe, ou « Tous » pour le mode de connexion.",
  "sessions.clearFilters": "Afficher tous les cours",
  "sessions.column.date": "Date",
  "sessions.column.group": "Groupe",
  "sessions.column.teacher": "Enseignant",
  "sessions.column.runs": "Scènes",
  "sessions.column.attendance": "Présents",
  "sessions.column.duration": "Durée",
  "sessions.column.mode": "Connexion",
  "sessions.filter.group": "Groupe",
  "sessions.filter.teacher": "Enseignant",
  "sessions.filter.mode": "Connexion",
  "sessions.minutes": "{count} min",
  "sessions.attendance": "{present} sur {total}",
  "sessions.live": "En direct",
  "sessions.ownerNotice":
    "Vous pouvez voir qu’un cours a eu lieu, ainsi que ses statistiques. Ce qui s’y est passé — qui a joué quel rôle, ce que l’enseignant a noté — appartient à l’enseignant qui l’a donné.",

  "review.title": "{group}, {date}",
  "review.tab.overview": "Vue d’ensemble",
  "review.tab.runs": "Scènes",
  "review.tab.scores": "Évaluations",
  "review.tab.notes": "Notes",
  "review.register": "Qui était là",
  "review.present": "Présent",
  "review.absent": "Absent",
  "review.run": "Scène {number}",
  "review.runScenario": "{title}, version {version}",
  "review.runDuration": "{count} min",
  "review.runAborted": "Arrêtée avant la fin",
  "review.cast": "Qui a joué quoi",
  "review.castAs": "dans le rôle de {role}",
  "review.difficulty": "Difficulté {level}",
  "review.timeline": "Étape par étape",
  "review.liveScene": "Scène en direct",
  "review.beat.line": "« {text} »",
  "review.beat.picture": "Image",
  "review.beat.pictureMissing": "Image indisponible",
  "review.noRuns": "Aucune scène n’a été jouée pendant ce cours",

  "review.scores.intro":
    "Facultatif, et cela le restera. Un cours sans évaluation est un cours ordinaire.",
  "review.scores.framework": "Évalué selon {name}",
  "review.scores.none": "Non évalué",
  "review.scores.clear": "Supprimer",
  "review.scores.saved": "C’est enregistré.",

  "review.notes.label": "Ce que vous voulez retenir",
  "review.notes.retention":
    "Visibles par vous uniquement et conservées jusqu’au {date}. Elles seront ensuite supprimées, avec tout ce qui concerne le déroulement de ce cours. Les statistiques restent.",
  "review.notes.saving": "Enregistrement…",
  "review.notes.saved": "Enregistré",
  "review.retentionSoon":
    "Le détail de ce cours sera supprimé le {date}. Les statistiques sont conservées.",
  "review.retentionGone":
    "Le détail de ce cours a été supprimé. Il en reste les statistiques.",
  "review.teacherOnly": "Seul l’enseignant qui a animé le cours peut en ouvrir le détail.",
  "review.notFound": "Ce cours est introuvable",
  "review.notFoundBody":
    "Le lien est peut-être ancien, ou le cours a dépassé la durée de conservation.",
  "review.allLessons": "Tous les cours",

  // ── le relevé de la salle (itération 2) ───────────────────────────
  "sessions.kind.room": "Cours en salle",
  "sessions.kind.show": "Soirée de spectacle",
  "review.lesson": "Cours",
  "review.tab.room": "Salle",
  "review.tab.show": "Spectacle",
  "review.dries.title": "Carte des trous de mémoire",
  "review.dries.intro":
    "Là où chaque personne a eu besoin de sa réplique\u00a0: soufflée par vous, ou demandée depuis la carte. Une étape compte une seule fois, quel que soit le nombre de souffles. Une information, jamais une note.",
  "review.dries.summary": "{count} trous · {prompted} soufflés · {asked} demandés",
  "review.dries.none": "Aucun trou",
  "review.dries.beat": "Étape {number}",
  "review.dries.offScript": "Une étape qui n’est plus dans la scène",
  "review.dries.prompted": "Soufflé\u00a0: {text}",
  "review.dries.promptedLine": "Soufflé",
  "review.dries.asked": "A demandé sa réplique",
  "review.dries.marks": "Marques par étape",
  "review.dries.mark.clean": "Étape {number}, {role}\u00a0: aucun trou",
  "review.dries.mark.prompted": "Étape {number}, {role}\u00a0: trou, soufflé",
  "review.dries.mark.asked": "Étape {number}, {role}\u00a0: trou, réplique demandée",
  "review.dries.mark.both": "Étape {number}, {role}\u00a0: trou, réplique demandée puis soufflée",
  "review.dries.legend": "● soufflé · ○ demandé",
  "review.rounds.title": "Tours",
  "review.rounds.intro": "Chaque tour joué\u00a0: l’exercice, qui était sur scène et combien de temps.",
  "review.rounds.round": "Tour {number}",
  "review.rounds.column.exercise": "Exercice",
  "review.rounds.column.who": "Sur scène",
  "review.rounds.column.length": "Durée",
  "review.rounds.untitled": "Tour sans nom",
  "review.rounds.empty": "Aucun tour n’a été joué pendant ce cours",
  "review.matrix.title": "Qui a travaillé avec qui",
  "review.matrix.intro":
    "Combien de fois chaque paire est montée ensemble, et combien de fois chaque personne est montée en tout.",
  "review.matrix.timesUp": "Passages",
  "review.matrix.cell": "{a} avec {b}\u00a0: {count}",
  "review.acts.title": "Numéros",
  "review.acts.intro": "L’ordre de passage tel qu’il a été joué.",
  "review.acts.act": "Numéro {number}",
  "review.acts.column.title": "Numéro",
  "review.acts.column.performers": "Sur scène",
  "review.acts.column.length": "Durée",
  "review.acts.untitled": "Numéro sans titre",
  "review.acts.empty": "Aucun numéro n’a été joué pendant ce cours",
  "review.jam.title": "Tirages du jam",
  "review.jam.draw": "Tirage {number}",
  "review.jam.group": "Groupe {number}",
  "review.jam.empty": "Aucun nom n’a été tiré",
  "group.history.dries.title": "Mémorisation du texte au fil des cours",
  "group.history.dries.intro":
    "Trous de mémoire par scène de texte, du cours le plus ancien au plus récent, uniquement pour les cours que vous avez animés. Une information, jamais une note.",
  "group.history.dries.entry": "{date}\u00a0: {count} trous sur {beats} étapes",

  // ── référentiels d’évaluation ─────────────────────────────────────
  "assessment.epa.name": "EPA / KMK",
  "assessment.epa.level1": "Reproduktion — reproduit ce qui a été montré",
  "assessment.epa.level2": "Reorganisation — combine des éléments connus d’une manière nouvelle",
  "assessment.epa.level3": "Transfer — l’applique à une situation inédite",
  "assessment.epa.level4": "Reflexion — juge ses propres choix et sait les justifier",
  "assessment.aoao.name": "AO1–AO4",
  "assessment.aoao.level1": "AO1 — crée et développe des idées",
  "assessment.aoao.level2": "AO2 — met en œuvre des compétences théâtrales",
  "assessment.aoao.level3": "AO3 — montre sa connaissance de la pratique",
  "assessment.aoao.level4": "AO4 — analyse et évalue son propre travail",
  "assessment.ncas.name": "NCAS",
  "assessment.ncas.level1": "Creating — imagine et produit",
  "assessment.ncas.level2": "Performing — réalise une œuvre artistique",
  "assessment.ncas.level3": "Responding — perçoit et interprète",
  "assessment.ncas.level4": "Connecting — relie l’œuvre au contexte et à l’expérience",
  "assessment.custom.name": "Personnalisé",
  "assessment.custom.level1": "1",
  "assessment.custom.level2": "2",
  "assessment.custom.level3": "3",
  "assessment.custom.level4": "4",

  // ── rapports ──────────────────────────────────────────────────────
  "reports.title": "Rapports",
  "reports.noStudentData":
    "Établi uniquement à partir de statistiques. Rien ici n’identifie un élève, ni ne le pourrait.",
  "reports.range.month": "Ce mois-ci",
  "reports.range.term": "Ce trimestre",
  "reports.range.year": "Cette année",
  "reports.range.custom": "Choisir les dates",
  "reports.range.from": "Du",
  "reports.range.to": "Au",
  "reports.export": "Télécharger en CSV",
  "reports.exported": "Téléchargé.",
  "reports.empty": "Rien ne s’est passé sur cette période",

  "reports.usage.title": "Utilisation",
  "reports.usage.sessions": "Cours",
  "reports.usage.runs": "Scènes",
  "reports.usage.groups": "Groupes ayant eu cours",
  "reports.usage.teachers": "Enseignants ayant donné des cours",
  "reports.usage.minutes": "Minutes de cours",
  "reports.usage.attendance": "Présence moyenne",
  "reports.usage.attendanceRate": "Taux de présence moyen",
  "reports.usage.online": "En ligne",
  "reports.usage.offline": "Hors ligne",
  "reports.usage.seats": "Places d’enseignant utilisées",
  "reports.usage.seatsOf": "{used} sur {total}",

  "reports.content.title": "Les scènes qui ont fonctionné",
  "reports.content.intro":
    "Une scène qu’un enseignant reprend est une scène qui a fonctionné. C’est ce que mesure le taux de reprise.",
  "reports.content.column.scenario": "Scène",
  "reports.content.column.uses": "Utilisations",
  "reports.content.column.repeatRate": "Taux de reprise",
  "reports.content.column.groups": "Groupes",
  "reports.content.column.teachers": "Enseignants",
  "reports.content.column.minutes": "Durée moyenne",

  "reports.activity.title": "Évolution",
  "reports.activity.column.month": "Mois",
  "reports.activity.column.sessions": "Cours",
  "reports.activity.column.runs": "Scènes",
  "reports.activity.column.groups": "Groupes",
  "reports.activity.column.minutes": "Minutes",

  "reports.teachers.title": "Activité des enseignants",
  "reports.teachers.usageNotPerformance":
    "De l’utilisation, pas de la performance. Ce n’est pas un classement, et ce n’est volontairement pas présenté comme tel — le temps passé sur un outil ne dit rien de la qualité d’un enseignement.",
  "reports.teachers.column.teacher": "Enseignant",
  "reports.teachers.column.sessions": "Cours",
  "reports.teachers.column.runs": "Scènes",
  "reports.teachers.column.groups": "Groupes",
  "reports.teachers.column.minutes": "Minutes",

  "reports.summary.title": "En un paragraphe",
  "reports.summary.generate": "Rédiger un résumé",
  "reports.summary.writing": "Rédaction…",
  "reports.summary.label":
    "Rédigé par l’assistant à partir des chiffres ci-dessus. Il ne voit jamais aucun nom — ni celui d’un élève, ni celui d’un enseignant.",
  "reports.teacherScope":
    "Vos propres groupes. La vue de l’ensemble de l’établissement est réservée au responsable.",

  // ── facturation ───────────────────────────────────────────────────
  "billing.title": "Facturation",
  "billing.plan.title": "Votre licence",
  "billing.plan.tier": "Formule",
  "billing.plan.seats": "Enseignants",
  "billing.plan.seatsOf": "{used} sur {total}",
  "billing.plan.renews": "Valable jusqu’au",
  "billing.plan.status": "Statut",
  "billing.plan.cancelAtPeriodEnd": "Prend fin à l’issue de cette période",
  "billing.tier.solo": "Solo",
  "billing.tier.small": "Petite",
  "billing.tier.medium": "Moyenne",
  "billing.tier.large": "Grande",
  "billing.tier.teachers": "Jusqu’à {count} enseignants",
  "billing.tier.teachersOne": "Un enseignant",
  "billing.tier.current": "Votre formule",
  "billing.tier.choose": "Passer à la formule {tier}",
  "billing.tier.blocked":
    "Vous avez {used} enseignants ; la formule {tier} en autorise {max}. Retirez-en d’abord quelques-uns, puis vous pourrez changer.",
  "billing.tier.priceElsewhere":
    "Les prix s’affichent au moment du paiement. Ils sont gérés dans Stripe : un changement de tarif ne nécessite donc jamais de mise à jour de l’application.",

  "billing.portal.title": "Moyen de paiement et factures",
  "billing.portal.body":
    "Les cartes bancaires, les factures et les reçus sont gérés par Stripe. Nous ne voyons jamais un numéro de carte.",
  "billing.portal.open": "Ouvrir le portail de facturation",

  "billing.state.expiring.title": "Votre licence expire le {date}",
  "billing.state.expiring.body":
    "Renouvelez-la d’ici là et rien ne change. Quoi qu’il arrive, rien n’est supprimé — vos scènes, vos groupes et vos statistiques restent exactement tels quels.",
  "billing.state.paymentFailed.title": "Un paiement a échoué",
  "billing.state.paymentFailed.body":
    "Les cours continuent. Stripe réessaiera la carte dans les prochains jours ; vous pouvez aussi la mettre à jour dès maintenant dans le portail de facturation.",
  "billing.state.expired.title": "Votre licence a expiré",
  "billing.state.expired.body":
    "Aucun nouveau cours ne peut commencer. Un cours déjà commencé n’est pas concerné, et rien n’est supprimé — vos scènes, groupes, rapports et statistiques vous appartiennent toujours et restent exportables.",
  "billing.state.manual.title": "Paiement sur facture",
  "billing.state.manual.body":
    "Votre licence relève d’un contrat et non d’un abonnement : il n’y a donc rien à modifier ici. Écrivez-nous et nous réglerons cela.",

  "billing.checkout.opening": "Ouverture du paiement…",
  "billing.checkout.legalFirst":
    "Vous devez accepter certains documents avant de pouvoir payer une licence. L’accord de traitement des données en fait partie : ce n’est pas un détail.",
  "billing.checkout.toLegal": "Les lire",
  "billing.checkout.waiting":
    "Le paiement est passé. Nous attendons la confirmation de Stripe — cela prend en général quelques secondes, et rien n’est perdu si vous quittez cette page.",
  "billing.checkout.cancelled": "Rien n’a été débité et rien n’a été modifié.",

  "billing.seatLimitInline":
    "Votre formule {tier} couvre {max} enseignants, et vous en avez déjà {used}. Passer à une formule supérieure prend une minute.",
  "billing.seatLimitAction": "Voir les formules",

  "nav.support": "Accès du support",
  "support.title": "Accès du support",
  "support.intro":
    "Spontar ne peut pas consulter le contenu de votre établissement de sa propre initiative. Si quelqu’un chez nous en a besoin, il vous le demande ici — vous décidez, l’accès est en lecture seule et se ferme automatiquement.",
  "support.empty": "Personne chez Spontar n’a demandé à consulter quoi que ce soit",
  "support.column.reason": "Motif invoqué",
  "support.column.asked": "Demandé le",
  "support.column.who": "Qui",
  "support.column.state": "État",
  "support.column.remaining": "Temps restant",
  "support.approve": "Autoriser l’accès",
  "support.deny": "Refuser",
  "support.revoke": "Fermer maintenant",
  "support.approved": "Accès accordé jusqu’à la fin de la durée choisie.",
  "support.denied": "Refusé. La personne a été prévenue.",
  "support.revoked": "Fermé.",
  "support.duration.1": "une heure",
  "support.duration.8": "une journée de travail",
  "support.duration.24": "une journée",
  "support.forHours": "Pour {duration}",
  "support.remaining": "{count} minutes restantes",
  "support.readOnly":
    "Toujours en lecture seule. Personne chez Spontar ne peut rien modifier dans votre établissement, et les notes et évaluations personnelles d’un enseignant ne sont jamais incluses.",

  "support.state.pending": "En attente de votre décision",
  "support.state.approved": "Ouvert",
  "support.state.denied": "Refusé par vous",
  "support.state.revoked": "Fermé par vous",
  "support.state.expired": "Fermé",

  "support.banner.active":
    "{who}, chez Spontar, consulte votre établissement pendant les {minutes} prochaines minutes, pour la raison suivante : {reason}",
  "support.banner.readOnly": "Lecture seule. Rien ne peut être modifié.",
  "support.banner.close": "Fermer maintenant",
  "support.banner.pending":
    "Quelqu’un chez Spontar a demandé à consulter votre établissement. Rien n’est ouvert tant que vous n’avez pas décidé.",
  "support.banner.decide": "Décider",

  "support.views.title": "Ce qui a été consulté",
  "support.views.empty": "Rien pour l’instant",
  "support.views.column.view": "Écran",
  "support.views.column.at": "Date et heure",

  "support.error.requestNotFound": "Cette demande est introuvable.",
  "support.error.alreadyDecided": "Cette demande a déjà été traitée.",
  "support.error.notApproved": "Cet accès n’est pas ouvert.",
  "support.error.notActive": "Cet accès est fermé.",
};
